import React, { useEffect, useState, useMemo } from "react";
import AuthService from "../../../api/services/AuthService";
import moment from "moment";
import LoaderHelper from "../../../customComponent/Loading/LoaderHelper";

const PayoutDetailsModal = ({ user, onClose }) => {
    const [payouts, setPayouts] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState("all");

    useEffect(() => {
        if (user?.id || user?._id) {
            loadPayoutHistory();
        }
    }, [user]);

    const loadPayoutHistory = async () => {
        try {
            LoaderHelper.loaderStatus(true);
            console.log("Fetching payout history for user:", user);
            // Using payoutHistoryAffiliate with user email as search parameter
            const response = await AuthService.payoutHistoryAffiliate(user._id);
            if (response?.success && response?.data?.data) {
                setPayouts(response.data.data);
            } else if (response?.success && Array.isArray(response?.data)) {
                setPayouts(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch payout history:", error);
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    // Group and sort payouts
    const groupedPayouts = useMemo(() => {
        let grouped = payouts.reduce((acc, payout) => {
            const batchId = payout.batchID || "No Batch";
            if (!acc[batchId]) {
                acc[batchId] = { data: [], date: payout.payoutDate };
            }
            acc[batchId].data.push(payout);
            return acc;
        }, {});

        // Sort by date descending
        const sortedEntries = Object.entries(grouped).sort(
            ([, a], [, b]) => new Date(b.date) - new Date(a.date)
        );

        return sortedEntries;
    }, [payouts]);

    // Get unique batch IDs for dropdown
    const batchOptions = useMemo(() => {
        return groupedPayouts.map(([batchId]) => batchId);
    }, [groupedPayouts]);

    // Filter payouts based on selected batch
    const filteredPayouts = useMemo(() => {
        if (selectedBatch === "all") {
            return groupedPayouts;
        }
        return groupedPayouts.filter(([batchId]) => batchId === selectedBatch);
    }, [groupedPayouts, selectedBatch]);

    // Calculate totals
    const totalPayouts = payouts.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const selfROI = payouts.filter(p => p.type === "self").reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const investorROI = payouts.filter(p => p.type !== "self").reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

    return (
        <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            💰 ROI Payout Details - {user?.firstName} {user?.lastName}
                        </h5>
                        <button className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body px-3">
                        {/* Summary Cards */}
                        <div className="row mb-4">
                            <div className="col-md-4">
                                <div className="card py-2" style={{ border: '1px solid #ffdc89' }}>
                                    <div className="card-body py-0 text-center">
                                        <h6 className="mb-2" style={{ color: '#888' }}>Total Payouts</h6>
                                        <h4 className="mb-0" style={{ color: '#ffdc89' }}>₹{totalPayouts.toLocaleString('en-IN')}</h4>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card py-2" style={{ border: '1px solid #ffdc89' }}>
                                    <div className="card-body py-0 text-center">
                                        <h6 className="mb-2" style={{ color: '#888' }}>Self ROI Received</h6>
                                        <h4 className="mb-0" style={{ color: '#ffdc89' }}>₹{selfROI.toLocaleString('en-IN')}</h4>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card py-2" style={{ border: '1px solid #ffdc89' }}>
                                    <div className="card-body py-0 text-center">
                                        <h6 className="mb-2" style={{ color: '#888' }}>Investors ROI Received</h6>
                                        <h4 className="mb-0" style={{ color: '#ffdc89' }}>₹{investorROI.toLocaleString('en-IN')}</h4>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Batch Filter */}
                        <div className="row mb-3 align-items-center">
                            <div className="col-md-2">
                                <label className="text-white">Filter by Batch:</label>
                            </div>
                            <div className="col-md-4">
                                <select 
                                    className="form-select" 
                                    value={selectedBatch} 
                                    onChange={(e) => setSelectedBatch(e.target.value)}
                                >
                                    <option value="all">All Batches ({groupedPayouts.length})</option>
                                    {batchOptions.map((batchId, idx) => {
                                        const batch = groupedPayouts.find(([id]) => id === batchId);
                                        const batchTotal = batch ? batch[1].data.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) : 0;
                                        return (
                                            <option key={idx} value={batchId} className="text-success">
                                                {batchId} - ₹{batchTotal.toLocaleString('en-IN')} ({batch[1].data.length} payouts)
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>

                        {/* Table with Scrollbar */}
                        <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'auto' }}>
                            <style>{`
                                .table-responsive::-webkit-scrollbar {
                                    width: 12px;
                                    height: 8px;
                                }
                                .table-responsive::-webkit-scrollbar-track {
                                    background: #555555ff;
                                    border-radius: 10px;
                                }
                                .table-responsive::-webkit-scrollbar-thumb {
                                    background: #888;
                                    border-radius: 10px;
                                }
                                .table-responsive::-webkit-scrollbar-thumb:hover {
                                    background: #555;
                                }
                                .batch-header {
                                    background: #393838a6;
                                    color: white;
                                    position: sticky;
                                    top: 0;
                                    z-index: 10;
                                }
                                .batch-total {
                                    background: #272727ff;
                                }
                                .payout-table thead th {
                                    position: sticky;
                                    top: 0;
                                    background: #393838;
                                    color: white;
                                    z-index: 11;
                                }
                            `}</style>
                            <table className="table table-hover table-bordered payout-table">
                                <thead>
                                    <tr>
                                        <th>S. No.</th>
                                        <th>Date</th>
                                        <th>Batch ID</th>
                                        <th>From</th>
                                        <th>Type</th>
                                        <th>Currency</th>
                                        <th>ROI%</th>
                                        <th>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPayouts.length > 0 ? (
                                        filteredPayouts.map(([batchId, batchData]) => {
                                            const rows = [];
                                            let globalIndex = 1;

                                            // Batch Header
                                            rows.push(
                                                <tr key={`header-${batchId}`} className="batch-header">
                                                    <td colSpan="8" className="text-center py-2 text-lg">
                                                        Batch: {batchId} | Date: {moment(batchData.date).format("DD-MM-YYYY")} | Payouts: {batchData.data.length} | Total : ₹{batchData.data.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toLocaleString('en-IN')}
                                                    </td>
                                                </tr>
                                            );

                                            // Batch Payouts
                                            batchData.data.forEach((payout, idx) => {
                                                rows.push(
                                                    <tr key={`${batchId}-${idx}`}>
                                                        <td>{globalIndex++}</td>
                                                        <td>{moment(payout.payoutDate).format("DD-MM-YYYY")}</td>
                                                        <td>{payout.batchID || "-"}</td>
                                                        <td>{payout.investorName || "ROI/Incentive from admin"}</td>
                                                        <td>
                                                            <span >
                                                                {payout.type === "self" ? "Self" : "Investor"}
                                                            </span>
                                                        </td>
                                                        <td>{payout.currency}</td>
                                                        <td>{payout?.self_roi_percent ? payout?.self_roi_percent : payout?.your_upline_percent}%</td>
                                                        <td className="text-end">₹{parseFloat(payout.amount || 0).toLocaleString('en-IN')}</td>
                                                    </tr>
                                                );
                                            });

                                            // Batch Total
                                            const batchTotal = batchData.data.reduce((sum, payout) => sum + parseFloat(payout.amount || 0), 0);
                                            rows.push(
                                                <tr key={`total-${batchId}`} className="batch-total">
                                                    <td colSpan="7" className="text-end py-2">
                                                        <strong>Batch Total:</strong>
                                                    </td>
                                                    <td className="text-end py-2">
                                                        ₹{batchTotal.toLocaleString('en-IN')}
                                                    </td>
                                                </tr>
                                            );

                                            return rows;
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-5">
                                                <div className="no_data_outer">
                                                    <div className="no_data_vector">
                                                        <img src="/images/Group 1171275449 (1).svg" alt="no-data" />
                                                    </div>
                                                    <p className="text-muted">No payout history found</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PayoutDetailsModal;
